import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { forwardRef } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import {
  buttonVariants,
  type ButtonVariant,
} from "@/components/ui/buttonVariants";
import { cn } from "@/lib/cn";

/**
 * Icon-only button — the canonical primitive for row-action pencils, overflow
 * triggers and other icon affordances across the admin/dealer panels.
 *
 * `label` is REQUIRED and is used for BOTH:
 *   - the accessible name (`aria-label`), and
 *   - the hover/focus tooltip (wired through the shared <Tooltip>).
 *
 * Because `label` is required by the type, every icon-only button gets an
 * accessible name + a tooltip by construction — there is no way to render a
 * bare, label-less icon button. The primitive itself has NO hardcoded copy;
 * the caller passes an already-translated i18n string as `label`.
 */
type IconButtonVariant = Extract<
  ButtonVariant,
  | "ghost"
  | "secondary"
  | "danger"
  // Outlined destructive — the mockup's modal-header trash (TASK-4836).
  | "danger-outline"
  | "destructive"
  | "primary"
>;

interface Props extends Omit<ComponentPropsWithoutRef<"button">, "aria-label"> {
  /** Lucide (or any) icon element rendered inside the button. */
  icon: ReactNode;
  /** Required accessible name — used as aria-label AND tooltip content. */
  label: string;
  variant?: IconButtonVariant;
  size?: "icon" | "icon-sm" | "icon-md";
  /** Tooltip side; defaults to "top". */
  tooltipSide?: "top" | "right" | "bottom" | "left";
}

export const IconButton = forwardRef<HTMLButtonElement, Props>(
  function IconButton(
    {
      icon,
      label,
      variant = "ghost",
      size = "icon-sm",
      tooltipSide = "top",
      className,
      type = "button",
      ...props
    },
    ref,
  ) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              ref={ref}
              type={type}
              aria-label={label}
              className={cn(buttonVariants({ variant, size }), className)}
              {...props}
            >
              <span aria-hidden="true" className="inline-flex">
                {icon}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide}>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
